import { useRef, useEffect } from 'react';
import { MapPin, Navigation, Share2, AlertTriangle, Flag, HandHelping } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { categories } from '@/constants/categories';

/**
 * Draggable Popup Content Wrapper
 * Enables drag and pinch-to-zoom on mobile
 */
export const DraggablePopupContent = ({ children, onMount }) => {
  const contentRef = useRef(null);
  
  useEffect(() => {
    if (contentRef.current && onMount) {
      const cleanup = onMount(contentRef.current);
      return cleanup;
    }
  }, [onMount]);
  
  return (
    <div ref={contentRef} className="popup-scalable">
      {/* Drag handle */}
      <div className="popup-drag-handle" title="Drag to move">
        <span className="sr-only">Drag to move popup</span>
      </div>
      {children}
      {/* Resize hint for mobile */}
      <div className="popup-resize-hint">
        Pinch to resize • Drag handle to move
      </div>
    </div>
  );
};

/**
 * Box Popup Content Component
 * Renders the content inside map marker popups
 */
const BoxPopupContent = ({
  box,
  user,
  isClaimingBox,
  onGetDirections,
  onShareToFacebook,
  onClaimBox,
  onReportBox
}) => {
  return (
    <Card className="border-0 shadow-none">
      <CardContent className="p-0">
        {/* Unclaimed badge */}
        {box.is_claimed === false && (
          <div className="bg-amber-100 border border-amber-300 rounded-lg p-2 mb-3 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-600" />
            <span className="text-amber-800 text-xs font-medium">Unclaimed Stall - Can you help?</span>
          </div>
        )}
        
        {/* Box Image */}
        {box.images && box.images.length > 0 && (
          <img
            src={box.images[0]}
            alt={box.name}
            className="w-full h-40 object-cover rounded-t-lg mb-3"
            data-testid={`box-image-${box.id}`}
            onError={(e) => {
              console.error(`Failed to load image for box ${box.id}:`, box.images[0]);
              e.target.style.display = 'none';
            }}
          />
        )}
        
        <div className="space-y-2">
          {/* Box Name */}
          <h3 className="text-xl font-bold text-gray-800" data-testid={`box-name-${box.id}`}>
            {box.name}
          </h3>
          
          {/* Category Badge */}
          <div className="inline-block px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">
            {categories.find(cat => cat.value === box.category)?.label || box.category}
          </div>
          
          {/* Description */}
          <p className="text-gray-600 text-sm" data-testid={`box-description-${box.id}`}>
            {box.description}
          </p>
          
          {/* Address */}
          <div className="flex items-start gap-2 text-sm text-gray-500">
            <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span data-testid={`box-address-${box.id}`}>{box.address}</span>
          </div>
          
          {/* Hours */}
          {box.hours && (
            <p className="text-sm text-gray-500" data-testid={`box-hours-${box.id}`}>
              <strong>Hours:</strong> {box.hours}
            </p>
          )}
          
          {/* Contact Info */}
          {box.contact_info && (
            <p className="text-sm text-gray-500" data-testid={`box-contact-${box.id}`}>
              <strong>Contact:</strong> {box.contact_info}
            </p>
          )}
          
          {/* Action buttons row */}
          <div className="flex gap-2 mt-3">
            {/* Get Directions Button */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
                  size="sm"
                  data-testid={`directions-btn-${box.id}`}
                >
                  <Navigation className="w-4 h-4 mr-1" />
                  Directions
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="z-[9999]">
                <DropdownMenuItem onSelect={() => onGetDirections('google', box)}>
                  Google Maps
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => onGetDirections('apple', box)}>
                  Apple Maps
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => onGetDirections('waze', box)}>
                  Waze
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => onGetDirections('osm', box)}>
                  OpenStreetMap
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            
            {/* Share to Facebook Button */}
            <Button
              onClick={() => onShareToFacebook(box)}
              variant="outline"
              className="border-blue-400 text-blue-600 hover:bg-blue-50"
              size="sm"
              data-testid={`share-btn-${box.id}`}
            >
              <Share2 className="w-4 h-4" />
            </Button>
          </div>
          
          {/* Claim/Report buttons for unclaimed boxes */}
          {box.is_claimed === false && (
            <div className="flex gap-2 mt-2">
              {user ? (
                <Button
                  onClick={() => onClaimBox(box)}
                  disabled={isClaimingBox}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                  size="sm"
                  data-testid={`claim-btn-${box.id}`}
                >
                  <HandHelping className="w-4 h-4 mr-1" />
                  {isClaimingBox ? 'Claiming...' : 'Claim This Stall'}
                </Button>
              ) : (
                <Button
                  onClick={() => onClaimBox(box)}
                  variant="outline"
                  className="flex-1 border-green-500 text-green-700"
                  size="sm"
                  data-testid={`login-to-claim-btn-${box.id}`}
                >
                  <HandHelping className="w-4 h-4 mr-1" />
                  Login to Claim
                </Button>
              )}
              <Button
                onClick={() => onReportBox(box)}
                variant="outline"
                className="border-red-300 text-red-600 hover:bg-red-50"
                size="sm"
                data-testid={`report-btn-${box.id}`}
              >
                <Flag className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default BoxPopupContent;
